import { inject, injectable } from 'inversify';

import type { IIndicationController, IIndicationStateManager } from '@/components/boxes/objectBoxes/interfaces';
import type * as THREE from 'three';
import type { IViewItemsViewModelController, IViewPortManager } from '@/engine/app/interfaces';
import { type IObjectViewModel, SceneItemKind } from '@/engine/app/interfaces';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { TYPES } from '@/engine/ioc';
import type { ViewsService } from '@/engine/app/Views.service';
import type { ViewEntity } from '@/engine/app/models/viewEntity/view.entity';
import type { IIndicationsViewModelFactory } from '@/engine/app/modules/indications/interfaces';
import { TYPES as INDICATION_TYPES } from '@/engine/app/modules/indications/iocTypes';
import {
  addCustomCommissurePointsViewModels,
  removeCustomCommissurePointsViewModels
} from '@/components/boxes/objectBoxes/CustomCommissurePointsBox/CustomCommissurePointsHelpers';
import { SliceType } from '@/engine/app/modules/images/interfaces';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { getClosestSplinePointToRay } from '@/engine/utils/geometryHelper';
import type { ICommissureCommandsFactory } from './interfaces';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { ICommand, ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command';
import type { ITTVRObjectsAlgoHelper } from '@/surgeries/cardiac/TTVR/interfaces';
import { TTVR_TYPES } from '@/surgeries/cardiac/TTVR/ioc/types';
import type { AlgorithmHelperFactory } from '@/surgeries/cardiac/TTVR/AlgorithmHelper/AlgorithmHelper.factory';
import { buildCommissureCommonAlgorithm } from '@/surgeries/cardiac/TTVR/TTVRCommissureAlgorithmBuilder';
import type { IndicationColorSetter } from '@/components/boxes/IndicationColorSetter';
import { getColorblind12Main } from '@/engine/utils/colorUtils';

@injectable()
export class CustomCommissurePointsIndicationController implements IIndicationController {
  private readonly _availableObjectsName = 'available_commissure_points';

  private readonly _extensionDescriptor = 'commissureCommonAlgorithm';

  private readonly _viewsService: ViewsService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _controller: IViewItemsViewModelController;

  private readonly _indicationColorSetter: IndicationColorSetter;

  private readonly _vmFactory: IIndicationsViewModelFactory;

  private readonly _objectsAlgoHelper: ITTVRObjectsAlgoHelper;

  private readonly _commissureCommandsFactory: ICommissureCommandsFactory;

  private readonly _commandProcessor: ICommandProcessor;

  private _viewModel: IObjectViewModel | undefined;

  private _stateManager: IIndicationStateManager | undefined;

  private _viewModelsAdded = false;

  private _descriptor: string | undefined;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(TYPES.IViewItemsViewModelController) controller: IViewItemsViewModelController,
    @inject(INDICATION_TYPES.IIndicationsViewModelFactory) vmFactory: IIndicationsViewModelFactory,
    @inject(TYPES.ICommissureCommandsFactory) commissureCommandsFactory: ICommissureCommandsFactory,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: ICommandProcessor,
    @inject(TYPES.IndicationColorSetter) indicationColorSetter: IndicationColorSetter,
    @inject(TTVR_TYPES.AlgorithmHelperFactory) algorithmHelperFactory: AlgorithmHelperFactory
  ) {
    this._viewsService = viewsService;
    this._planningAlgorithm = planningAlgorithm;
    this._controller = controller;
    this._vmFactory = vmFactory;
    this._indicationColorSetter = indicationColorSetter;
    this._objectsAlgoHelper = algorithmHelperFactory.createObjectAlgorithmHelper(
      this._planningAlgorithm,
      this._availableObjectsName,
      this._extensionDescriptor,
      buildCommissureCommonAlgorithm
    );
    this._commissureCommandsFactory = commissureCommandsFactory;
    this._commandProcessor = commandProcessor;
    const currentView: ViewEntity | null = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('Current view has to be defined');
    }
  }

  public setStateManager(stateManager: IIndicationStateManager): void {
    this._stateManager = stateManager;
  }

  public async initializeIndication(descriptor: string, label: string, pointNumber: number | undefined): Promise<void> {
    if (pointNumber === undefined) {
      throw new Error('Commissure point indication should have its number!')
    }

    this._viewModel = this._vmFactory.createViewModel(
      SceneItemKind.CommissurePoint,
      undefined,
      descriptor,
      ['device_roi', 'landing_zone_spline', 'tp.' + descriptor + '_rotation'],
      label,
      SliceType.SAGITTAL,
      true,
      false
    );

    this._indicationColorSetter.setIndicationPointColor(this._viewModel, getColorblind12Main()[pointNumber]);

    this._descriptor = descriptor;
    await this._objectsAlgoHelper.addObjectAsync(descriptor);
  }

  public async indicate(vpm: IViewPortManager, screenPoint: THREE.Vector2): Promise<boolean> {
    if (!this._viewModel) {
      console.warn('ViewModel has to be defined');
      return false;
    }

    const spline = (await this._planningAlgorithm.getValueAsync('landing_zone_spline', DT.Spline))?.value;

    if (!spline) {
      console.warn('Landing Zone Spline has to be defined');
      return false;
    }

    const viewRay = vpm.getCameraController().getViewRay(screenPoint);

    const minPt = getClosestSplinePointToRay(spline.splinePts, viewRay);

    if (!minPt) {
      return false;
    }

    const vector = minPt.sub(spline.center).cross(spline.normal).negate().cross(spline.normal);

    await this._planningAlgorithm.setValueAsync(
      this._getVectorDescriptor(this._viewModel.descriptor),
      new DT.Vector(vector)
    );

    if (!this._viewModelsAdded) {
      this._addViewModel(this._viewModel);
    }

    this._controller.locatePointByDescriptor(this._viewModel.descriptor);

    return true;
  }

  private _cleanup(): void {
    this._viewModel = undefined;
    this._viewModelsAdded = false;
    this._descriptor = undefined;
    this._stateManager?.setIndicationState(false);
  }

  public async cancelIndication(): Promise<void> {
    if (!this._viewModel || !this._descriptor) {
      console.warn('View model has to be defined');
      return;
    }
    this._removeViewModel(this._viewModel);
    await this._objectsAlgoHelper.deleteObjectAsync(this._descriptor);
    this._cleanup();
  }

  public async finalizeIndication(vpm: IViewPortManager, screenPoint: THREE.Vector2): Promise<boolean> {
    if (this._viewModel && this._viewModelsAdded) {
      const vectorDescriptor = this._getVectorDescriptor(this._viewModel.descriptor);
      const vector = await this._planningAlgorithm.getValueAsync(vectorDescriptor, DT.Vector);
      const pointViewModel = this._viewModel.clone();
      const ret = await this.indicate(vpm, screenPoint);
      if (ret && vector) {
        await this.cancelIndication();

        const command: ICommand = this._commissureCommandsFactory.createAddCommissureCommand(
          this._viewsService.currentViewOrThrow,
          vector.value,
          pointViewModel,
          vectorDescriptor,
          this._objectsAlgoHelper
        );

        await this._commandProcessor.execute(command);
        this._cleanup();
        return true;
      } else {
        return false;
      }
    } else {
      this._cleanup();
      return true;
    }
  }

  public async removeIndicated(descriptor: string): Promise<void> {
    const currentView = this._viewsService.currentViewOrThrow;
    const pointViewModel = currentView.getObjects().find((obj) => obj.descriptor === descriptor);
    if (!pointViewModel) {
      console.warn('Can not find labeled cusp point');
      return;
    }
    const vectorDescriptor = this._getVectorDescriptor(pointViewModel.descriptor);
    const vector = await this._planningAlgorithm.getValueAsync(vectorDescriptor, DT.Vector);
    if (!vector) {
      throw new Error('Vector is undefined');
    }
    const command: ICommand = this._commissureCommandsFactory.createRemoveCommissureCommand(
      this._viewsService.currentViewOrThrow,
      vector.value,
      pointViewModel,
      vectorDescriptor,
      this._objectsAlgoHelper
    );
    await this._commandProcessor.execute(command);
  }

  public suspendIndication(): void {
    if (!this._viewModel) {
      console.warn('ViewModel has to be defined');
      return;
    }
    this._removeViewModel(this._viewModel);
  }

  private _addViewModel(viewModel: IObjectViewModel): void {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('Current view have to be defined');
    }
    addCustomCommissurePointsViewModels(currentView, this._viewsService, viewModel);
    this._viewModelsAdded = true;
  }

  private _getVectorDescriptor(pointDescriptor: string): string {
    return 'vec_' + pointDescriptor + '_indicated';
  }

  private _removeViewModel(viewModel: IObjectViewModel): void {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('Current view have to be defined');
    }
    removeCustomCommissurePointsViewModels(currentView, this._viewsService, viewModel);
    this._viewModelsAdded = false;
  }
}
